import React from 'react'
import { graphql } from 'gatsby'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'

import Seo from '../components/seo'
import Layout from '../components/layout'
import NewsLayout from '../components/layouts/news'

import pageAlternatesUrls from '../utils/pageAlternatesUrls'
import GetNewsroomUrls from '../utils/getNewsroomUrls'

function News({data, pageContext}) {
  const mainContent = data.mainContent
  const newsroomUrls = GetNewsroomUrls()
  const urls = pageAlternatesUrls(mainContent, data.alternateContent, 'title', `/${newsroomUrls['Actualités']['fr']}/`, `/${newsroomUrls['Actualités']['en']}/`, pageContext.locale)

  return (
    <Layout 
      frUrl={urls.frUrl}
      enUrl={urls.enUrl} 
      initLocale={pageContext.locale}
      mainTag={mainContent.mainTag.title}
      headerType="bg-white"
    >
      <Seo
        frUrl={urls.frUrl}
        enUrl={urls.enUrl}
        page={{
          title: mainContent.seoTitle ? mainContent.seoTitle : mainContent.title,
          description: mainContent.seoDescription ? mainContent.seoDescription.seoDescription : documentToPlainTextString(JSON.parse(mainContent.chapo.raw)),
          image: (mainContent.mainImage && mainContent.mainImage.file && mainContent.mainImage.file.file && mainContent.mainImage.file.file.url) ? mainContent.mainImage.file.file.url : null,
          path: (pageContext.locale === 'fr') ? urls.frUrl : urls.enUrl
        }}
      />
      <NewsLayout
        currentUrl={(pageContext.locale === 'fr') ? urls.frUrl : urls.enUrl}
        data={mainContent}
        allNews={data.allNews.nodes} />
    </Layout>
  );
}

export const pageQuery = graphql`
  query SingleNews($contentful_id: String!, $locale: String!) {
    mainContent: contentfulNews(contentful_id: { eq: $contentful_id }, node_locale: { eq: $locale }) {
      title
      internal {
        type
      }
      contentful_id
      seoTitle
      seoDescription {
        seoDescription
      }
      node_locale
      publicationDate
      document {
        file {
          url
        }
      }
      pressRelease {
        annexes {
          file {
            url
          }
        }
        pdf {
          file {
            url
            details {
              size
            }
          }
        }
      }
      mainImage {
        altImage
        file {
          file {
            url
          }
        }
      }
      mainTag {
        title
      }
      profile {
        title
      }
      chapo {
        raw
        references {
          __typename
          ... on ContentfulStyledText {
            contentful_id
            text
            type
          }
          ... on ContentfulNewsroomList {
            contentful_id
            title
            slug
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulNews {
            contentful_id
            title
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulSolution {
            contentful_id
            title
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulLanding {
            contentful_id
            title
            slug
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulOffer {
            solution {
              title
            }
            contentful_id
            title
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulEvent {
            contentful_id
            title
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulContact {
            contentful_id
            title
            slug
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulSimulator {
            contentful_id
            title
            slug
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulHub {
            contentful_id
            title
            slug
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulHomepageSolutions {
            contentful_id
            title
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulPage {
            contentful_id
            title
            slug
            customType
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulAsset {
            contentful_id
            file {
              url
            }
          }
        }
      }
      node_locale
      slices {
        ... on Node {
          ... on ContentfulSliceImage {
            internal {
              type
            }
            node_locale
            titleContent
            link
            image {
              altImage
              file {
                file {
                  url
                }
              }
            }
          }
          ... on ContentfulFullText {
            internal {
              type
            }
            displayTitle
            title
            switchDecoPlacement
            cta {
              pageLink {
                ... on Node {
                  ... on ContentfulEvent {
                    title
                    node_locale
                    internal {
                      type
                    }
                  } 
                  ... on ContentfulNews {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSolution {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulLanding {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulOffer {
                    solution {
                      title
                    }
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHomepageSolutions {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulContact {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSimulator {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHub {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulPage {
                    title
                    slug
                    customType
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNewsroomList {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                }
              }
              label
              customLink
              assetLink {
                file {
                  url
                }
              }
            }
            content {
              raw
              references {
                __typename
                ... on ContentfulStyledText {
                  contentful_id
                  text
                  type
                }
                ... on ContentfulTestimony {
                  contentful_id
                  image {
                    altImage
                    file {
                      url
                    }
                  }
                  themeSolutions
                  imageShape
                  content {
                    content
                  }
                  author
                  job
                  theme
                }
                ... on ContentfulVideo {
                  contentful_id
                  title
                  suptitle
                  youtubeId
                  youtubePlaylistId
                  cover {
                    file {
                      url
                    }
                  }
                }
                ... on ContentfulFrontImg {
                  contentful_id
                  file {
                    url
                  }
                  altImage
                  legend {
                    legend
                  }
                }
                ... on ContentfulNewsroomList {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulNews {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulSolution {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulLanding {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulOffer {
                  solution {
                    title
                  }
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulEvent {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulContact {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulSimulator {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulHub {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulHomepageSolutions {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulPage {
                  contentful_id
                  title
                  slug
                  customType
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulAsset {
                  contentful_id
                  file {
                    url
                  }
                }
              }
            }
            node_locale
          }
          ... on ContentfulImageTexteSection {
            internal {
              type
            }
            node_locale
            title
            themeSolutions
            imageShape
            proportions
            content {
              raw
              references {
                __typename
                ... on ContentfulStyledText {
                  contentful_id
                  text
                  type
                }
                ... on ContentfulNewsroomList {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulNews {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulEvent {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulSolution {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulLanding {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulOffer {
                  solution {
                    title
                  }
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulContact {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulSimulator {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulHub {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulHomepageSolutions {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulPage {
                  contentful_id
                  title
                  slug
                  customType
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulAsset {
                  contentful_id
                  file {
                    url
                  }
                }
              }
            }
            cta {
              pageLink {
                ... on Node {
                  ... on ContentfulEvent {
                    title
                    node_locale
                    internal {
                      type
                    }
                  } 
                  ... on ContentfulNews {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSolution {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulLanding {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulOffer {
                    solution {
                      title
                    }
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHomepageSolutions {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulContact {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSimulator {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHub {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulPage {
                    title
                    slug
                    customType
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNewsroomList {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                }
              }
              customLink
              label
              assetLink {
                file {
                  url
                }
              }
            }
            image {
              altImage
              file {
                file {
                  url
                }
              }
            }
            reversed
            theme
          }
          ... on ContentfulTabsaccordion {
            internal {
              type
            }
            type
            titleContent
            cta {
              pageLink {
                ... on Node {
                  ... on ContentfulEvent {
                    title
                    node_locale
                    internal {
                      type
                    }
                  } 
                  ... on ContentfulNews {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSolution {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulLanding {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulOffer {
                    solution {
                      title
                    }
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHomepageSolutions {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulContact {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSimulator {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHub {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulPage {
                    title
                    slug
                    customType
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNewsroomList {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                }
              }
              label
              customLink
              assetLink {
                file {
                  url
                }
              }
            }
            items {
              title
              subtitle1
              subtitle2
              content {
                raw
                references {
                  __typename
                  ... on ContentfulStyledText {
                    contentful_id
                    text
                    type
                  }
                  ... on ContentfulNewsroomList {
                    contentful_id
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNews {
                    contentful_id
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSolution {
                    contentful_id
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulLanding {
                    contentful_id
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulOffer {
                    solution {
                      title
                    }
                    contentful_id
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulEvent {
                    contentful_id
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulContact {
                    contentful_id
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSimulator {
                    contentful_id
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHub {
                    contentful_id
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHomepageSolutions {
                    contentful_id
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulPage {
                    contentful_id
                    title
                    slug
                    customType
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulAsset {
                    contentful_id
                    file {
                      url
                    }
                  }
                }
              }
              cta {
                pageLink {
                  ... on Node {
                    ... on ContentfulEvent {
                      title
                      node_locale
                      internal {
                        type
                      }
                    } 
                    ... on ContentfulNews {
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulSolution {
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulLanding {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulOffer {
                      solution {
                        title
                      }
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulHomepageSolutions {
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulContact {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulSimulator {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulHub {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulPage {
                      title
                      slug
                      customType
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulNewsroomList {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                  }
                }
                label
                customLink
                assetLink {
                  file {
                    url
                  }
                }
              }
              contactItems {
                title
                icon
                link {
                  pageLink {
                    ... on Node {
                      ... on ContentfulEvent {
                        title
                        node_locale
                        internal {
                          type
                        }
                      } 
                      ... on ContentfulNews {
                        title
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulSolution {
                        title
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulLanding {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulOffer {
                        solution {
                          title
                        }
                        title
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulHomepageSolutions {
                        title
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulContact {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulSimulator {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulHub {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulPage {
                        title
                        slug
                        customType
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulNewsroomList {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                    }
                  }
                  customLink
                  label
                  assetLink {
                    file {
                      url
                    }
                  }
                }
                content {
                  raw
                }
              }
            }
          }
          ... on ContentfulVideo {
            internal {
              type
            }
            title
            suptitle
            youtubeId
            youtubePlaylistId
            cover {
              file {
                url
              }
            }
          }
          ... on ContentfulTestimony {
            internal {
              type
            }
            themeSolutions
            imageShape
            author
            job
            theme
            content {
              content
            }
            image {
              altImage
              file {
                file {
                  url
                }
              }
            }
          }
          ... on ContentfulSliderPartnersClients {
            internal {
              type
            }
            title
            items {
              logo {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
              link
              noFollow
            }
          }
          ... on ContentfulSlider {
            internal {
              type
            }
            title
            type
            items {
              ... on Node {
                ... on ContentfulFrontImg {
                  altImage
                  file {
                    file {
                      url
                    }
                  }
                  legend:childContentfulFrontImgLegendTextNode {
                    legend
                  }
                }
                ... on ContentfulShortItem {
                  title
                  text {
                    text
                  }
                }
              }
            }
          }
        }
      }
      pushPages {
        chapo {
          chapo
        }
        ctaLabel
        page {
          ... on Node {
            ... on ContentfulLanding {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              slug
              node_locale
            }
            ... on ContentfulOffer {
              internal {
                type
              }
              mainTag {
                title
              }
              solution {
                title
              }
              title
              node_locale
            }
            ... on ContentfulSolution {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              node_locale
            }
            ... on ContentfulSimulator{
              internal {
                type
              }
              mainTag {
                title
              }
              title
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulEvent {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulNews {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              publicationDate
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulPage {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              slug
              customType
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulContact {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              slug
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulSimulator {
              title
              mainTag {
                title
              }
              slug
              node_locale
              internal {
                type
              }
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulHub {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              slug
              node_locale
            }
            ... on ContentfulNewsroomList {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              slug
              node_locale
            }
          }
        }
      }
    }

    alternateContent: contentfulNews(contentful_id: { eq: $contentful_id }, node_locale: { ne: $locale }, title: {ne: null}) {
      title
      node_locale
    }

    allNews: allContentfulNews(filter: {distributionChannel: {elemMatch: {title: {eq: "Terega solutions"}}}, node_locale: {eq: $locale},  title: {ne: null}}, sort: {fields: publicationDate, order: DESC}) {
      nodes {
        title
        internal {
          type
        }
        publicationDate
        contentful_id
        mainImage {
          altImage
          file {
            file {
              url
            }
          }
        }
        node_locale
      }
    }
  }
`;

export default News